/**
 * Live-infra view controls (owner round 2, DECISIONS #47), mounted on the
 * diagram pane's view strip beside the Infrastructure tab (DiagramPane's
 * RuntimeStripControls, 2026-09-05), over the one cached overlay query:
 *
 *  - `LiveEnvSwitcher` — the compact environment switcher, one pill per
 *    environment the overlay lists. The env choice lives at VIEW time
 *    (persisted per repo, prod default); the runtime map's live numbers
 *    follow it by reading the overlay's per-env bucket. Switching is a pure
 *    client-side lookup — one poll already carries every environment.
 *  - `LiveMapCallout` — the one-time post-connect discoverability nudge
 *    ("Live from Render · updated every minute"), shown while armed and
 *    dismissed on the first interaction.
 */

import { useEffect } from 'react';

import './liveinfra.css';

import { useLiveViewStore, useViewEnv } from './envStore';
import { envGlance, envSummaryLine } from './liveModel';
import { clampViewEnv, viewEnvironmentsOf, type LiveOverlay, type ViewEnvironment } from './types';
import { CloseIcon, IconButton } from '../../lib/ui';

/** Exact callout copy (owner item 4) — tests pin this string. */
export const LIVE_CALLOUT_COPY = 'Live from Render · updated every minute';

export function LiveEnvSwitcher({
  repoId,
  overlay,
}: {
  repoId: string;
  /** the cached overlay (for the per-env counts); optional so the bare
   * switcher stays usable in isolation */
  overlay?: LiveOverlay | null;
}) {
  const env = clampViewEnv(useViewEnv(repoId), viewEnvironmentsOf(overlay));
  const setViewEnv = useLiveViewStore((s) => s.setViewEnv);
  const glance = envGlance(overlay, env);
  // Bare pills (2026-09-05; owner: "this can be designed better"): the
  // control rides the pane's view strip beside the Infrastructure tab, and
  // prod / staging / dev need no caption there.
  return (
    <div className="lm-live-env-block">
      <div
        className="lm-segmented"
        role="group"
        aria-label="Live environment"
        title={
          glance !== null
            ? `${envSummaryLine(env, glance)} · the guide's live numbers follow this choice`
            : "Which environment the guide's live numbers show"
        }
        data-testid="live-env-switcher"
      >
        {viewEnvironmentsOf(overlay).map((option: ViewEnvironment) => {
          // One clean segmented control (owner 2026-08-27: counts on every
          // chip + a caption repeating them read as clutter). Each segment's
          // tooltip still carries its own population, so the difference
          // between environments is one hover away.
          const g = envGlance(overlay, option);
          return (
            <button
              key={option}
              type="button"
              className="lm-segmented-option"
              aria-pressed={env === option}
              onClick={() => setViewEnv(repoId, option)}
              data-testid={`env-view-${option}`}
              title={g !== null ? envSummaryLine(option, g) : undefined}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function LiveMapCallout({ repoId }: { repoId: string }) {
  const pending = useLiveViewStore((s) => s.calloutPendingByRepo[repoId] === true);
  const dismiss = useLiveViewStore((s) => s.dismissCallout);

  // Dismissed on interaction: the first click/key anywhere after the
  // callout renders clears it (listeners attach post-mount, so the click
  // that navigated here never counts). Its own button also dismisses.
  // On CLICK, not pointerdown (2026-09-02): the callout sits above the env
  // switcher, and clearing it on pointerdown moved the switcher up before
  // the click landed, so the first press on an environment hit nothing.
  useEffect(() => {
    if (!pending) return;
    const onInteract = () => dismiss(repoId);
    window.addEventListener('click', onInteract, { capture: true });
    window.addEventListener('keydown', onInteract, { capture: true });
    return () => {
      window.removeEventListener('click', onInteract, { capture: true });
      window.removeEventListener('keydown', onInteract, { capture: true });
    };
  }, [pending, repoId, dismiss]);

  if (!pending) return null;
  return (
    <div className="lm-live-callout" role="status" data-testid="live-map-callout">
      <span className="lm-live-callout-dot" aria-hidden />
      <span>{LIVE_CALLOUT_COPY}</span>
      <IconButton kind="ghost" size="sm" label="Dismiss" onClick={() => dismiss(repoId)}>
        <CloseIcon size={12} />
      </IconButton>
    </div>
  );
}
